import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/stores/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { StreamPlayer } from '@/components/streams';
import { CommentSection } from '@/components/streams/comment-section';
import { PollingSection, PollCreator } from '@/components/streams/polling-section';
import { InviteCodeRedemption } from '@/components/streams/invite-code-redemption';
import { RealtimeViewerCount } from '@/components/streams/realtime-viewer-count';
import { LikeButton } from '@/components/streams/like-button';
import { StreamShareDialog } from '@/components/streams/stream-share-dialog';
import { RelatedStreamsCarousel } from '@/components/streams/RelatedStreamsCarousel';
import { api } from '@/lib/api/client';
import { SharedStream } from '@/lib/shared-types';
import type { StreamDetailData } from '@/lib/shared-types/api/endpoints/streams';
import { ArrowLeft, MessageSquare, BarChart2, Share2, ChevronDown, ChevronUp, Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import { useDurableObjectChannel } from '@/hooks/use-durable-object-channel';

function formatViewCount(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return count.toLocaleString();
}

function formatDate(dateString?: string) {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function resolveReminderState(payload: unknown): boolean | null {
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  const directBooleanKeys = [
    'reminderSet',
    'isReminderSet',
    'hasReminder',
    'currentUserReminderSet',
    'viewerReminderSet',
  ];

  for (const key of directBooleanKeys) {
    if (typeof record[key] === 'boolean') {
      return record[key] as boolean;
    }
  }

  if (record.reminder && typeof record.reminder === 'object') {
    return resolveReminderState(record.reminder);
  }

  if (record.viewer && typeof record.viewer === 'object') {
    return resolveReminderState(record.viewer);
  }

  return null;
}

export default function StreamDetailPage() {
  const { streamId } = useParams<{ streamId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [stream, setStream] = useState<SharedStream | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [reminderSet, setReminderSet] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);

  // Use ref to track stream status for polling without causing effect re-runs
  const streamStatusRef = useRef<string | null>(null);

  // Debug logging (only in development)
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('StreamDetailPage - streamId param:', streamId);
    }
  }, [streamId]);

  const fetchStream = useCallback(async () => {
    if (!streamId || streamId === 'NaN' || isNaN(parseInt(streamId, 10))) {
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const response = await api.get<StreamDetailData>(`/api/streams/${streamId}`);

      if (response.success && response.data?.stream) {
        const backendStream = response.data.stream;
        const transformedStream: SharedStream = {
          ...backendStream,
          tags: backendStream.tags_array || []
        };
        setStream(transformedStream);

        const backendReminderState =
          resolveReminderState(response.data) ?? resolveReminderState(backendStream);
        if (backendReminderState !== null) {
          setReminderSet(backendReminderState);
        }
      } else {
        setError(response.error?.message || 'Failed to load stream');
      }
    } catch (err) {
      console.error('Error fetching stream:', err);
      setError('Failed to load stream');
    } finally {
      setLoading(false);
    }
  }, [streamId]);

  useEffect(() => {
    if (!streamId || streamId === 'NaN' || isNaN(parseInt(streamId, 10))) {
      console.error('StreamDetailPage - Invalid streamId:', streamId);
      setError('Invalid stream ID');
      setLoading(false);
      return;
    }
    fetchStream();
  }, [streamId, fetchStream]);

  // Keep ref in sync with stream status
  useEffect(() => {
    streamStatusRef.current = stream?.status || null;
  }, [stream?.status]);

  // Real-time WebSocket subscription for stream status updates
  const handleStreamStatusMessage = useCallback((message: any) => {
    if (message.type === 'stream_status_changed' && message.streamId === parseInt(streamId || '0')) {
      if (stream) {
        setStream({ ...stream, status: message.status });
        streamStatusRef.current = message.status;
      }

      if (message.status === 'live') {
        api.get<StreamDetailData>(`/api/streams/${streamId}`).then((response) => {
          if (response.success && response.data?.stream) {
            const backendStream = response.data.stream;
            setStream({ ...backendStream, tags: backendStream.tags_array || [] });
          }
        }).catch((err) => {
          console.error('Error refreshing stream after status change:', err);
          toast.error('Stream is live but failed to load updated details. Please refresh.');
        });
      }
    }
  }, [streamId, stream]);

  useDurableObjectChannel({
    channel: streamId ? `stream:status:${streamId}` : '',
    enabled: !!streamId && streamStatusRef.current !== 'ended',
    onMessage: handleStreamStatusMessage,
  });

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-4xl px-4 py-4 sm:px-6 sm:py-6">
        <div className="py-10 text-center sm:py-12">
          <LoadingSpinner size="lg" />
          <h2 className="text-xl font-semibold mt-4">Loading stream...</h2>
        </div>
      </div>
    );
  }

  if (error || !stream) {
    return (
      <div className="mx-auto w-full max-w-4xl px-4 py-4 sm:px-6 sm:py-6">
        <div className="py-10 text-center sm:py-12">
          <h2 className="text-2xl font-bold text-destructive mb-4">
            {error || 'Stream not found'}
          </h2>
          <Button onClick={() => navigate('/streams')} variant="outline">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  const isLive = stream.status === 'live';
  const isScheduled = stream.status === 'scheduled';
  const hasDescription = !!stream.description;
  const descTruncated = hasDescription && stream.description!.length > 250;

  const handleSetReminder = async () => {
    if (!user) { toast.error('Sign in to set a reminder'); return; }
    if (reminderSet) return;
    setReminderLoading(true);
    setReminderSet(true);
    try {
      const response = await api.post(`/api/streams/${streamId}/reminder`, {});
      if (response.success) {
        const backendReminderState = resolveReminderState(response.data);
        setReminderSet(backendReminderState ?? true);
        toast.success('Reminder set');
      } else {
        const msg = (response.error?.message || '').toLowerCase();
        if (msg.includes('already')) {
          setReminderSet(true);
          toast.success('Reminder set');
        } else {
          setReminderSet(false);
          toast.error(response.error?.message || 'Failed to set reminder');
        }
      }
    } catch {
      setReminderSet(false);
      toast.error('Failed to set reminder');
    } finally {
      setReminderLoading(false);
    }
  };

  return (
    <div className="@container/stream-detail mx-auto w-full max-w-[1800px] px-3 py-3 @[700px]/stream-detail:px-4 @[700px]/stream-detail:py-4 @[1100px]/stream-detail:px-6 @[1100px]/stream-detail:py-6">

      {/* Back nav + live viewer count */}
      <div className="mb-3 flex items-center justify-between">
        <Button onClick={() => navigate('/streams')} variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to streams
        </Button>
        <RealtimeViewerCount streamId={stream.id} />
      </div>

      {/* ── Main two-column grid ── */}
      <div className="grid grid-cols-1 gap-4 @[1100px]/stream-detail:grid-cols-4 @[1100px]/stream-detail:gap-6">

        {/* ── LEFT COLUMN — player + YouTube-style info ── */}
        <div className="@[1100px]/stream-detail:col-span-3 space-y-0">

          {/* Player */}
          <StreamPlayer
            stream={stream}
            className="w-full aspect-video rounded-xl overflow-hidden"
          />

          {/* Sign-in prompt for unauthenticated users on non-public streams */}
          {!user && stream.visibility !== 'public' && !stream.has_access && (
            <div className="mt-3">
              <Card>
                <CardContent className="py-6 text-center space-y-3">
                  <p className="font-medium">Sign in to watch this stream</p>
                  <p className="text-sm text-muted-foreground">
                    {stream.visibility === 'invite_only'
                      ? 'This stream requires an invitation or direct access grant.'
                      : 'This stream is available to members. Sign in to continue.'}
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <Button onClick={() => navigate('/signin')} size="sm">Sign In</Button>
                    <Button onClick={() => navigate('/auth/register')} variant="outline" size="sm">Create Account</Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Invite code gate — only for logged-in users on invite_only streams */}
          {user && (stream.inviteRequired || stream.visibility === 'invite_only') && stream.has_access === false && (
            <div className="mt-3">
              <InviteCodeRedemption
                streamId={stream.id}
                streamTitle={stream.title}
                variant="inline"
                onAccessGranted={fetchStream}
              />
            </div>
          )}

          {/* ── Title (YouTube style: bold, below player) ── */}
          <h1 className="mt-3 text-xl font-bold leading-snug">
            {stream.title}
          </h1>

          {/* ── Channel row + Actions (Like / Share) ── */}
          <div className="mt-3 flex items-center justify-between flex-wrap gap-3 pb-3 border-b">
            {/* Channel avatar + name */}
            {stream.creator && (
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarImage src={stream.creator.profilePictureUrl || undefined} />
                  <AvatarFallback className="text-base font-semibold">
                    {stream.creator.name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-sm leading-tight">{stream.creator.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {stream.viewerCount !== undefined
                      ? `${formatViewCount(stream.viewerCount)} viewing`
                      : 'Channel'}
                  </p>
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-2">
              <LikeButton
                userId={user?.id ?? 0}
                streamId={stream.id.toString()}
                initialLikes={stream.likes || 0}
                initialIsLiked={stream.isLiked ?? false}
                variant="outline"
                size="md"
              />
              {isScheduled && (
                <Button
                  variant={reminderSet ? 'default' : 'outline'}
                  size="sm"
                  onClick={handleSetReminder}
                  disabled={reminderLoading || reminderSet}
                >
                  {reminderSet
                    ? <><BellOff className="h-4 w-4 mr-2" />Reminder set</>
                    : <><Bell className="h-4 w-4 mr-2" />Remind me</>
                  }
                </Button>
              )}
              <StreamShareDialog streamId={stream.id.toString()} streamTitle={stream.title}>
                <Button variant="outline" size="sm">
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </StreamShareDialog>
            </div>
          </div>

          {/* ── Description box (YouTube's rounded gray box) ── */}
          {(hasDescription || stream.scheduledStart || (stream.tags && stream.tags.length > 0)) && (
            <div className="mt-3 rounded-xl bg-muted/50 p-3 space-y-2">
              {/* Views + date + status badge */}
              <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-sm font-medium">
                {stream.viewerCount !== undefined && (
                  <span>{formatViewCount(stream.viewerCount)} views</span>
                )}
                {stream.scheduledStart && (
                  <>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-muted-foreground">{formatDate(stream.scheduledStart)}</span>
                  </>
                )}
                <Badge
                  variant={isLive ? 'destructive' : 'secondary'}
                  className="ml-1 text-xs capitalize"
                >
                  {isLive ? '● LIVE' : stream.status}
                </Badge>
              </div>

              {/* Description text — expandable */}
              {hasDescription && (
                <div>
                  <p className={`text-sm text-muted-foreground whitespace-pre-wrap break-words ${
                    descriptionExpanded ? '' : 'line-clamp-3'
                  }`}>
                    {stream.description}
                  </p>
                  {descTruncated && (
                    <button
                      onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                      className="mt-1 text-sm font-medium hover:text-foreground flex items-center gap-0.5"
                    >
                      {descriptionExpanded
                        ? <><ChevronUp className="h-4 w-4" /> Show less</>
                        : <><ChevronDown className="h-4 w-4" /> Show more</>
                      }
                    </button>
                  )}
                </div>
              )}

              {/* Tags */}
              {stream.tags && stream.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {stream.tags.map((tag, i) => (
                    <span key={i} className="text-primary text-sm">#{tag}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Comments — VOD only (live chat is in the right panel) ── */}
          {!isLive && stream?.id && (
            <div className="mt-6">
              <h2 className="text-lg font-semibold mb-4">Comments</h2>
              <CommentSection streamId={stream.id} isLive={false} />
            </div>
          )}
        </div>

        {/* ── RIGHT COLUMN — context-aware ── */}
        <div className="@[1100px]/stream-detail:col-span-1">
          {isLive ? (
            /* LIVE: sticky chat + polls panel */
            <div className="sticky top-6 h-[calc(100vh-7rem)] flex flex-col border rounded-xl overflow-hidden">
              <Tabs defaultValue="chat" className="flex flex-col h-full">
                <TabsList className="w-full grid grid-cols-2 rounded-none border-b h-11 shrink-0 bg-muted/30">
                  <TabsTrigger value="chat" className="rounded-none text-xs gap-1.5">
                    <MessageSquare className="h-3.5 w-3.5" />
                    Chat
                  </TabsTrigger>
                  <TabsTrigger value="polls" className="rounded-none text-xs gap-1.5">
                    <BarChart2 className="h-3.5 w-3.5" />
                    Polls
                  </TabsTrigger>
                </TabsList>

                <TabsContent
                  value="chat"
                  className="flex-1 overflow-hidden m-0 data-[state=active]:flex data-[state=active]:flex-col"
                >
                  {stream?.id && (
                    <CommentSection streamId={stream.id} isLive={true} />
                  )}
                </TabsContent>

                <TabsContent value="polls" className="flex-1 overflow-auto p-3 m-0 space-y-3">
                  <PollingSection streamId={stream.id.toString()} isLive={true} />
                  {(user?.role?.name === 'admin' || user?.role?.name === 'media_team') && (
                    <PollCreator streamId={stream.id.toString()} />
                  )}
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            /* VOD: related streams sidebar (YouTube recommendations style) */
            <div className="sticky top-6 space-y-4">
              <RelatedStreamsCarousel
                currentStreamId={stream.id}
                tags={stream.tags}
              />
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
